import React, { useEffect, useState, useCallback } from 'react';
import { MarketplaceItem, UserRole, MarketplaceItemStatus, MarketplaceOrder, Transaction } from '../types';
import { 
  ShoppingBag, Search, X, Plus, UploadCloud, MoreVertical, Edit, Trash2, History, ImageIcon 
} from 'lucide-react';
import { 
  checkContentForViolation as checkTextForViolation,
  MODERATION_WARNING_TEXT
} from '../services/moderationService';
import { 
  analyzeFileContent,
  ADVANCED_MODERATION_WARNING_TEXT
} from '../services/advancedModerationService';
import { useToast } from '../contexts/ToastContext';
import { API_BASE_URL } from '@/config';
import { useAuth } from '@/contexts/AuthContext';
import { useDropzone } from 'react-dropzone';
import Cropper from 'react-easy-crop';
import { Point, Area } from 'react-easy-crop/types';
import getCroppedImg from '../lib/cropImage';
import DualPaymentModal from './DualPaymentModal';
import { dataURLtoFile } from '../lib/utils';

// =======================================================================================
// TYPE DEFINITIONS
// =======================================================================================

interface MarketplaceProps {
  onBlockUser: () => void;
  onChat: (seller: { name: string; avatar: string }) => void;
  currentUserRole: UserRole;
  currentUserId: string;
}

type ItemFormData = {
  title: string;
  description: string;
  price: string;
  type: 'NFT' | 'Physical Art' | 'Ticket' | 'Instrument' | 'Equipment';
  quantity: string;
};

interface PurchaseData {
  order: MarketplaceOrder;
  transaction: Transaction;
  item: MarketplaceItem;
}

// =======================================================================================
// HELPER & SUB-COMPONENTS
// =======================================================================================

const ImageUploader: React.FC<{ onImageReady: (base64: string) => void, initialImageUrl?: string | null }> = ({ onImageReady, initialImageUrl }) => {
  const [imageSrc, setImageSrc] = useState<string | null>(null);
  const [crop, setCrop] = useState<Point>({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
  const { notify } = useToast();

  useEffect(() => {
    setImageSrc(initialImageUrl || null);
    setZoom(1);
    setCrop({ x: 0, y: 0 });
  }, [initialImageUrl]);

  const onDrop = useCallback((acceptedFiles: File[]) => {
    if (acceptedFiles.length > 0) {
      const file = acceptedFiles[0];
      const reader = new FileReader();
      reader.addEventListener('load', () => setImageSrc(reader.result as string));
      reader.readAsDataURL(file);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop, accept: { 'image/*': ['.jpeg', '.png', '.gif', '.jpg'] }, multiple: false });

  const onCropComplete = useCallback((_c: Area, croppedPixels: Area) => setCroppedAreaPixels(croppedPixels), []);

  const handleApplyCrop = async () => {
    if (imageSrc && croppedAreaPixels && imageSrc.startsWith('data:image')) {
      try {
        const croppedImageBase64 = await getCroppedImg(imageSrc, croppedAreaPixels);
        onImageReady(croppedImageBase64);
        setImageSrc(croppedImageBase64);
        notify('Image cropped and applied!', 'success');
      } catch (e) {
        console.error(e);
        notify('Could not process image. Please try another one.', 'error');
      }
    } else {
      notify('Please drop a new image file before applying crop.', 'warn');
    }
  };

  return (
    <div className="space-y-4">
      {!imageSrc || !imageSrc.startsWith('data:image') ? (
        <div {...getRootProps()} className={`p-10 border-2 border-dashed rounded-xl text-center cursor-pointer transition-colors ${isDragActive ? 'border-kala-secondary bg-kala-secondary/10' : 'border-kala-700 hover:border-kala-600'}`}>
          <input {...getInputProps()} />
          {initialImageUrl && <img src={initialImageUrl} alt="Current item" className="w-32 h-24 object-cover rounded-lg mx-auto mb-4"/>}
          <UploadCloud className="mx-auto w-12 h-12 text-kala-400 mb-2" />
          <p className="text-kala-300">Drop a new image here, or click to select</p>
          <p className="text-xs text-kala-500 mt-1">800x600 recommended. You can crop after selecting.</p>
        </div>
      ) : (
        <div className="space-y-4 flex flex-col items-center">
          <div className="relative w-full h-64 bg-kala-800 rounded-lg">
            <Cropper image={imageSrc} crop={crop} zoom={zoom} aspect={4 / 3} onCropChange={setCrop} onZoomChange={setZoom} onCropComplete={onCropComplete} />
          </div>
          <div className='flex flex-col sm:flex-row items-center gap-2 w-full'>
            <button onClick={handleApplyCrop} className="w-full sm:w-auto flex-grow py-2 px-4 bg-kala-secondary text-kala-900 font-bold rounded-lg">Apply Crop</button>
            <button onClick={() => setImageSrc(initialImageUrl || null)} className="w-full sm:w-auto py-2 px-4 bg-red-500/20 text-red-300 font-bold rounded-lg">Cancel Change</button>
          </div>
        </div>
      )}
    </div>
  );
};

const ItemImageDisplay = ({ imageUrl, title, className = 'w-full h-full' }: { imageUrl?: string | null, title: string, className?: string }) => {
  if (imageUrl) {
    return <img src={imageUrl} alt={title} className={`${className} object-cover`} />;
  }
  return (
    <div className={`${className} bg-kala-800 flex items-center justify-center`}>
      <div className="text-center text-kala-500">
        <ImageIcon className="mx-auto w-10 h-10 mb-2" />
        <p className="text-xs font-semibold">No Image</p>
      </div>
    </div>
  );
};

const ListModal = ({ formData, setFormData, closeModal, handleSaveItem, editingItem, filters, onImageReady }: { formData: ItemFormData; setFormData: (data: ItemFormData) => void; closeModal: () => void; handleSaveItem: () => void; editingItem: MarketplaceItem | null; filters: string[]; onImageReady: (b64: string) => void; }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm" onClick={closeModal}>
    <div className="bg-kala-900 border border-kala-700 rounded-2xl w-full max-w-lg shadow-2xl" onClick={e => e.stopPropagation()}>
      <div className="p-4 bg-kala-800 border-b border-kala-700 flex justify-between items-center">
        <h3 className="text-white font-bold">{editingItem ? 'Edit Item' : 'List New Item'}</h3>
        <button onClick={closeModal} className="text-kala-400 hover:text-white"><X className="w-5 h-5" /></button>
      </div>
      <div className="p-6 space-y-4 max-h-[80vh] overflow-y-auto">
        <p className="text-xs text-orange-200 bg-orange-900/20 p-3 rounded-lg border border-orange-500/30">{MODERATION_WARNING_TEXT} All item text is reviewed.</p>
        <input type="text" value={formData.title} onChange={(e) => setFormData({...formData, title: e.target.value})} className="w-full bg-kala-800 border-kala-700 rounded-lg p-2 text-white" placeholder="Item Title"/>
        <textarea value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})} className="w-full bg-kala-800 border-kala-700 rounded-lg p-2 text-white" placeholder="Item Description" rows={4}/>
        <div className="grid grid-cols-2 gap-4">
          <input type="number" value={formData.price} onChange={(e) => setFormData({...formData, price: e.target.value})} className="w-full bg-kala-800 border-kala-700 rounded-lg p-2 text-white" placeholder="Price (USD)"/>
          <select value={formData.type} onChange={(e) => setFormData({ ...formData, type: e.target.value as ItemFormData['type'] })} className="w-full bg-kala-800 border-kala-700 rounded-lg p-2 text-white">
              {filters.filter(f => f !== 'All').map(f => <option key={f} value={f}>{f}</option>)}
          </select>
          <input type="number" value={formData.quantity} onChange={(e) => setFormData({...formData, quantity: e.target.value})} className="w-full bg-kala-800 border-kala-700 rounded-lg p-2 text-white" placeholder="Quantity" min="1"/>
        </div>
        <div className="mt-4">
            <label className="block text-sm font-medium text-kala-300 mb-2">Item Image</label>
            <p className="text-xs text-red-200 bg-red-900/20 p-3 rounded-lg border border-red-500/30 mb-2">{ADVANCED_MODERATION_WARNING_TEXT}</p>
            <ImageUploader onImageReady={onImageReady} initialImageUrl={editingItem?.imageUrl} />
        </div>
        <button onClick={handleSaveItem} className="w-full py-3 bg-kala-secondary text-kala-900 font-bold rounded-xl mt-4">{editingItem ? 'Save Changes' : 'Publish Listing'}</button>
      </div>
    </div>
  </div>
);

const OrderCard = ({ order, perspective }: { order: MarketplaceOrder, perspective: 'buyer' | 'seller' }) => (
    <div className="bg-kala-900/50 p-3 rounded-lg border border-kala-700 flex items-center gap-4">
      <ItemImageDisplay imageUrl={order.item.imageUrl} title={order.item.title} className="w-12 h-12 rounded-md" />
      <div className="flex-1">
        <p className="font-bold text-sm text-white truncate">{order.item.title}</p>
        <p className="text-xs text-kala-400">
          {perspective === 'buyer' ? `Bought from ${order.item.seller.name}` : `Sold to ${order.buyer?.name || 'a user'}`}
        </p>
      </div>
      <div className="text-right">
          <p className="font-mono font-bold text-sm text-kala-secondary">{order.priceAtPurchase} {order.item.currency}</p>
          <p className="text-xs text-kala-500">{new Date(order.createdAt).toLocaleDateString()}</p>
      </div>
    </div>
);

const OrdersView = ({ purchases, sales }: { purchases: MarketplaceOrder[], sales: MarketplaceOrder[] }) => (
    <div className="bg-kala-800/30 border border-kala-700 rounded-xl p-4 md:p-6">
      <h3 className="font-bold text-white text-lg mb-4">Order History</h3>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div>
          <h4 className="font-bold text-kala-secondary mb-3">My Purchases ({purchases.length})</h4>
          <div className="space-y-3 max-h-96 overflow-y-auto pr-2">
            {purchases.length > 0 ? purchases.map(order => <OrderCard key={order.id} order={order} perspective="buyer" />) : <p className="text-sm text-kala-400">You haven't purchased any items yet.</p>}
          </div>
        </div>
        <div>
          <h4 className="font-bold text-kala-secondary mb-3">My Sales ({sales.length})</h4>
          <div className="space-y-3 max-h-96 overflow-y-auto pr-2">
              {sales.length > 0 ? sales.map(order => <OrderCard key={order.id} order={order} perspective="seller" />) : <p className="text-sm text-kala-400">You haven't sold any items yet.</p>}
          </div>
        </div>
      </div>
    </div>
);

// =======================================================================================
// MAIN COMPONENT
// =======================================================================================

const Marketplace: React.FC<MarketplaceProps> = ({ onBlockUser, onChat, currentUserRole, currentUserId }) => {
  const { currentUser, token } = useAuth();
  const { notify } = useToast();

  const [marketItems, setMarketItems] = useState<MarketplaceItem[]>([]);
  const [myOrders, setMyOrders] = useState<MarketplaceOrder[]>([]);
  const [view, setView] = useState('marketplace');
  const [filter, setFilter] = useState('All');
  const [viewingItem, setViewingItem] = useState<MarketplaceItem | null>(null);
  const [purchaseData, setPurchaseData] = useState<PurchaseData | null>(null);
  const [isListModalOpen, setIsListModalOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const [editingItem, setEditingItem] = useState<MarketplaceItem | null>(null);
  const [newCroppedImage, setNewCroppedImage] = useState<string | null>(null);

  const [formData, setFormData] = useState<ItemFormData>({ title: '', description: '', price: '', type: 'Physical Art', quantity: '1' });

  const getAuthHeaders = () => {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    };
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    return headers;
  };

  const fetchMarketplaceData = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/marketplace`);
      if (!response.ok) throw new Error('Failed to fetch marketplace items.');
      const items: MarketplaceItem[] = await response.json();
      setMarketItems(items);
    } catch (error: any) {
      console.error(error);
      notify(error.message || 'Could not load marketplace items.', 'error');
    }
  }, [notify]);

  const fetchOrderData = useCallback(async () => {
    try {
        const response = await fetch(`${API_BASE_URL}/api/marketplace/orders/my-purchases`, {
            headers: getAuthHeaders(),
        });
        if (!response.ok) throw new Error('Failed to fetch your orders.');
        const orders: MarketplaceOrder[] = await response.json();
        setMyOrders(orders);
    } catch (error: any) {
        console.error('Failed to fetch orders:', error);
        notify(error.message || 'Could not load your orders.', 'error');
    }
  }, [token, notify]);

  useEffect(() => { 
    fetchMarketplaceData();
    fetchOrderData();
  }, [fetchMarketplaceData, fetchOrderData]);

  const handleSaveItem = async () => {
    if (!formData.title || !formData.price || !formData.quantity) {
        return notify('Title, price, and quantity are required.', 'error');
    }

    // Layer 1: Basic text profanity check
    const combinedText = `${formData.title} ${formData.description}`;
    if (checkTextForViolation(combinedText)) {
        closeModal();
        notify(`Listing blocked: Text contains forbidden terms.`, 'error');
        return onBlockUser();
    }

    let imageObjectName = editingItem ? editingItem.image : null;

    // Layer 2: Advanced content-aware check for the image
    if (newCroppedImage) {
        notify('Scanning image for violations...', 'info');
        try {
            const imageFile = dataURLtoFile(newCroppedImage, `${formData.title.replace(/\s+/g, '_') || 'marketplace_image'}.png`);
            const imageAnalysis = await analyzeFileContent(imageFile);

            if (imageAnalysis.violation) {
                closeModal();
                notify(`Image upload blocked: ${imageAnalysis.reason}`, 'error');
                return onBlockUser();
            }
            
            notify('Image passed moderation. Uploading...', 'info');
            const uploadResponse = await fetch(`${API_BASE_URL}/api/marketplace/upload`, {
                method: 'POST',
                headers: getAuthHeaders(),
                body: JSON.stringify({ image: newCroppedImage }),
            });
            if (!uploadResponse.ok) throw await uploadResponse.json();
            const { objectName } = await uploadResponse.json();
            imageObjectName = objectName;
            notify('Image uploaded!', 'success');
        } catch (err: any) {
            console.error('Image processing failed:', err);
            return notify(err.error || 'Failed to process image.', 'error');
        }
    }

    if (!imageObjectName && !editingItem) return notify('A new listing requires an item image.', 'error');

    const isEditing = editingItem !== null;
    const url = isEditing ? `${API_BASE_URL}/api/marketplace/${editingItem.id}` : `${API_BASE_URL}/api/marketplace`;
    const method = isEditing ? 'PUT' : 'POST';
    const payload = { ...formData, price: parseFloat(formData.price), quantity: parseInt(formData.quantity, 10), image: imageObjectName };

    try {
      const response = await fetch(url, {
        method,
        headers: getAuthHeaders(),
        body: JSON.stringify(payload),
      });
      if (!response.ok) throw await response.json();
      notify(`Item ${isEditing ? 'updated' : 'listed'} successfully! Pending review.`, 'success');
      closeModal();
      fetchMarketplaceData();
    } catch (err: any) {
      console.error(`Failed to ${method} item:`, err);
      notify(err.error || 'Failed to save item.', 'error');
    }
  };

  const handleDeleteItem = async (itemId: string) => {
    if (!window.confirm('Are you sure you want to permanently delete this item?')) return;
    try {
      const response = await fetch(`${API_BASE_URL}/api/marketplace/${itemId}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });
      if (response.status !== 204) throw await response.json();
      notify('Item deleted successfully.', 'success');
      fetchMarketplaceData();
      setActiveDropdown(null);
      if (viewingItem?.id === itemId) setViewingItem(null);
    } catch (err: any) {
      console.error(err);
      notify(err.error || 'Failed to delete item.', 'error');
    }
  };

  const startPurchase = async (item: MarketplaceItem) => {
    setViewingItem(null);
    notify('Preparing your order...', 'info');

    try {
      const response = await fetch(`${API_BASE_URL}/api/marketplace/orders`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ itemId: item.id, quantity: 1 }),
      });

      const responseData = await response.json();
      if (!response.ok) throw new Error(responseData.error || 'Could not create order.');
      
      setPurchaseData({
        order: responseData.order,
        transaction: responseData.transaction,
        item: item 
      });

    } catch (error: any) {
      console.error('Failed to start purchase:', error);
      notify(error.message, 'error');
      fetchMarketplaceData();
    }
  };

  const handlePurchaseSuccess = (sellerName: string) => {
      notify(`Purchase successful! You will receive an email with instructions on how to coordinate with the seller, ${sellerName}, for delivery.`, 'success', 10000);
      setPurchaseData(null);
      fetchMarketplaceData();
      fetchOrderData();
  };
  
  const closeDualPaymentModal = () => {
    setPurchaseData(null);
    fetchMarketplaceData(); 
  };

  const closeModal = () => {
    setIsListModalOpen(false);
    setEditingItem(null);
    setNewCroppedImage(null);
  };

  const openCreateModal = () => {
    setEditingItem(null);
    setFormData({ title: '', description: '', price: '', type: 'Physical Art', quantity: '1' });
    setNewCroppedImage(null);
    setIsListModalOpen(true);
  };

  const openEditModal = (item: MarketplaceItem) => {
    setEditingItem(item);
    setFormData({ title: item.title, description: item.description || '', price: String(item.price), type: item.type, quantity: String(item.quantity) });
    setNewCroppedImage(null);
    setIsListModalOpen(true);
    setActiveDropdown(null);
  };

  const filters = ['All', 'NFT', 'Physical Art', 'Instrument', 'Ticket', 'Equipment'];
  const displayedItems = (filter === 'All' ? marketItems : marketItems.filter(i => i.type === filter))
    .filter(item => item.status !== MarketplaceItemStatus.REJECTED);
  
  const mySales = myOrders.filter(order => order.item.sellerId === currentUserId);
  const myPurchases = myOrders.filter(order => order.buyerId === currentUserId);

  return (
    <div className="space-y-6 relative" onClick={() => activeDropdown && setActiveDropdown(null)}>
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div><h2 className="text-2xl font-bold text-white flex items-center gap-2"><ShoppingBag className="text-kala-secondary" /> Marketplace</h2></div>
        <div className="flex items-center gap-2">
            <button onClick={() => setView('marketplace')} className={`px-4 py-2 rounded-lg font-bold text-sm ${view === 'marketplace' ? 'bg-kala-secondary text-kala-900' : 'bg-kala-800 text-kala-300'}`}>Store</button>
            <button onClick={() => setView('orders')} className={`px-4 py-2 rounded-lg font-bold text-sm ${view === 'orders' ? 'bg-kala-secondary text-kala-900' : 'bg-kala-800 text-kala-300'} flex items-center gap-2`}><History className="w-4 h-4"/>My Orders</button>
            <button onClick={openCreateModal} className="bg-green-500 text-white font-bold px-4 py-2 rounded-lg hover:bg-green-600 transition-colors flex items-center gap-2"><Plus className="w-4 h-4" /> List Item</button>
        </div>
      </div>

      {view === 'marketplace' ? (
        <>
            <div className="bg-kala-800/30 border border-kala-700 rounded-xl p-4 flex flex-col md:flex-row gap-4 items-center">
                <div className="relative w-full md:w-96">
                <Search className="absolute left-3 top-2.5 w-4 h-4 text-kala-500" />
                <input type="text" placeholder="Search..." className="w-full bg-kala-900 border border-kala-700 rounded-lg pl-10 pr-4 py-2 text-white focus:ring-1 focus:ring-kala-secondary outline-none" />
                </div>
                <div className="flex gap-2 overflow-x-auto w-full md:w-auto pb-2 md:pb-0">
                {filters.map(f => <button key={f} onClick={() => setFilter(f)} className={`px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap transition-colors ${filter === f ? 'bg-kala-700 text-white' : 'bg-kala-900 text-kala-400 hover:bg-kala-800'}`}>{f}</button>)}
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {displayedItems.map((item) => {
                const canManage = currentUserRole === UserRole.SYSTEM_ADMIN_LIVE || currentUserRole === UserRole.ADMIN || item.sellerId === currentUserId;
                return (
                    <div key={item.id} className="bg-kala-800/50 border border-kala-700 rounded-xl overflow-hidden group flex flex-col">
                        <div className="relative h-48 bg-black cursor-pointer group" onClick={() => setViewingItem(item)}>
                            <ItemImageDisplay imageUrl={item.imageUrl} title={item.title} className="w-full h-full transition-transform duration-500 group-hover:scale-110" />
                            <div className="absolute top-3 right-3 bg-black/60 backdrop-blur px-2 py-1 rounded text-xs text-white font-bold">{item.type}</div>
                            {item.status !== 'APPROVED' && <div className={`absolute bottom-0 left-0 right-0 p-2 text-center text-xs font-bold text-white bg-${item.status === 'PENDING' ? 'orange' : 'red'}-500/80`}>{item.status}</div>}
                        </div>
                        <div className="p-4 flex-1 flex flex-col">
                            <div className="flex items-center justify-between mb-2">
                                <div className="flex items-center gap-2 cursor-pointer" onClick={() => setViewingItem(item)}>
                                    <img src={item.seller.avatar || 'https://via.placeholder.com/50'} alt={item.seller.name} className="w-5 h-5 rounded-full"/>
                                    <span className="text-xs text-kala-300 truncate max-w-[80px]">{item.seller.name}</span>
                                </div>
                                {canManage && <div className="relative">
                                    <button onClick={(e) => { e.stopPropagation(); setActiveDropdown(activeDropdown === item.id ? null : item.id); }} className="text-kala-400 hover:text-white p-1"><MoreVertical className="w-4 h-4" /></button>
                                    {activeDropdown === item.id && <div className="absolute right-0 mt-2 w-36 bg-kala-800 border border-kala-700 rounded-md shadow-lg z-20 py-1" onClick={e => e.stopPropagation()}>
                                    <button onClick={() => openEditModal(item)} className="w-full text-left px-3 py-1.5 text-sm text-kala-300 hover:bg-kala-700 flex items-center gap-2"><Edit className="w-3 h-3" /> Edit</button>
                                    <button onClick={() => handleDeleteItem(item.id)} className="w-full text-left px-3 py-1.5 text-sm text-red-400 hover:bg-red-500/10 flex items-center gap-2"><Trash2 className="w-3 h-3" /> Delete</button>
                                    </div>}
                                </div>}
                            </div>
                            <h3 className="font-bold text-slate-100 mb-1 truncate cursor-pointer" onClick={() => setViewingItem(item)}>{item.title}</h3>
                            <div className="mt-auto pt-3 border-t border-kala-700/50 flex items-center justify-between">
                                <div>
                                    <p className="text-[10px] text-kala-400 uppercase">Price</p>
                                    <p className="text-lg font-mono font-bold text-kala-secondary">{item.price} {item.currency}</p>
                                </div>
                                <button onClick={() => setViewingItem(item)} className="px-3 py-2 rounded-lg bg-kala-secondary/80 text-kala-900 hover:bg-kala-secondary font-bold text-xs" disabled={item.quantity === 0 || item.status === 'SOLD'}>{item.status === 'SOLD' ? 'Sold Out' : 'View Details'}</button>
                            </div>
                        </div>
                    </div>
                );
                })}
            </div>
        </>
      ) : (
        <OrdersView purchases={myPurchases} sales={mySales} />
      )}

      {viewingItem && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-in fade-in" onClick={() => setViewingItem(null)}>
              <div className="bg-kala-900 border border-kala-700 rounded-2xl w-full max-w-3xl shadow-2xl flex flex-col md:flex-row max-h-[90vh]" onClick={e => e.stopPropagation()}>
                  <div className="w-full md:w-1/2 h-64 md:h-auto bg-black relative">
                      <ItemImageDisplay imageUrl={viewingItem.imageUrl} title={viewingItem.title} className="w-full h-full" />
                  </div>
                  <div className="w-full md:w-1/2 flex flex-col p-6 overflow-y-auto">
                      <div className="flex justify-between items-start mb-4">
                          <h2 className="text-2xl font-bold text-white leading-tight">{viewingItem.title}</h2>
                          <button onClick={() => setViewingItem(null)} className="text-kala-400 hover:text-white"><X className="w-6 h-6" /></button>
                      </div>
                      <div className="flex items-center justify-between gap-2 mb-6">
                          <span className="text-3xl font-mono font-bold text-kala-secondary">{viewingItem.price} {viewingItem.currency}</span>
                          <span className="px-3 py-1 rounded bg-kala-800 text-kala-300 text-sm border border-kala-700">{viewingItem.quantity} Available</span>
                      </div>
                      <div className="space-y-4 flex-1">
                          {viewingItem.description && <p className="text-slate-300 text-sm leading-relaxed whitespace-pre-line">{viewingItem.description}</p>}
                      </div>
                      <div className="mt-8 pt-6 border-t border-kala-800 flex gap-4">
                          <button onClick={() => { setViewingItem(null); onChat(viewingItem.seller); }} className="flex-1 py-3 bg-kala-800 hover:bg-kala-700 text-white font-bold rounded-xl border border-kala-600">Chat with Seller</button>
                          <button onClick={() => startPurchase(viewingItem)} className="flex-1 py-3 bg-kala-secondary hover:bg-cyan-400 text-kala-900 font-bold rounded-xl" disabled={viewingItem.quantity === 0 || viewingItem.status === 'SOLD'}>{viewingItem.status === 'SOLD' ? 'Sold Out' : 'Buy Now'}</button>
                      </div>
                  </div>
              </div>
          </div>
      )}
      
      {purchaseData && (
        <DualPaymentModal
          order={purchaseData.order}
          transaction={purchaseData.transaction}
          serviceName={purchaseData.item.title}
          onSuccess={() => handlePurchaseSuccess(purchaseData.item.seller.name)}
          onClose={closeDualPaymentModal}
          onCancel={closeDualPaymentModal}
          token={token}
        />
      )}

      {isListModalOpen && <ListModal 
          formData={formData} 
          setFormData={setFormData} 
          closeModal={closeModal} 
          handleSaveItem={handleSaveItem} 
          editingItem={editingItem} 
          filters={filters} 
          onImageReady={setNewCroppedImage} />
      }
    </div>
  );
};

export default Marketplace;